import { RefCallback, useCallback, useRef, useState } from "react";

export const DEFAULT_SCROLL_FADE_HEIGHT_PX = 24;

/**
 * Which clipped edges get a fade. Containers whose bottom edge is occupied by
 * something opaque and sticky — a chat composer, say — want `"top"`: a bottom
 * fade there would wash out the sticky element instead of the content behind it.
 */
export type ScrollFadeEdges = "both" | "top" | "bottom";

export type ScrollFadeOptions = {
  /**
   * Only fade once the user has actually scrolled away from the top. By
   * default a scrollable container shows its bottom fade even at rest — a
   * deliberate "more below" affordance (the audience rail's chat list relies
   * on it). Regions whose resting composition must render unmasked — e.g.
   * shadowed cards sitting at the container's bottom edge, which the mask
   * would dim — opt in so fades appear mid-scroll and clear again at the top.
   */
  fadeOnlyAfterScroll?: boolean;
};

export const useScrollFade = (
  fadeHeightPx: number = DEFAULT_SCROLL_FADE_HEIGHT_PX,
  edgesToFade: ScrollFadeEdges = "both",
  { fadeOnlyAfterScroll = false }: ScrollFadeOptions = {},
): {
  ref: RefCallback<HTMLDivElement>;
  style: React.CSSProperties;
} => {
  const elRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [showTop, setShowTop] = useState(false);
  const [showBottom, setShowBottom] = useState(false);

  const update = useCallback(() => {
    const el = elRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const isScrollable = scrollHeight > clientHeight;
    const hasScrolled = scrollTop > 1;

    setShowTop(edgesToFade !== "bottom" && isScrollable && hasScrolled);
    setShowBottom(
      edgesToFade !== "top" &&
        isScrollable &&
        (hasScrolled || !fadeOnlyAfterScroll) &&
        scrollTop + clientHeight < scrollHeight - 1,
    );
  }, [edgesToFade, fadeOnlyAfterScroll]);

  const refCallback: RefCallback<HTMLDivElement> = useCallback(
    (el: HTMLDivElement | null) => {
      const prevEl = elRef.current;
      if (prevEl) {
        prevEl.removeEventListener("scroll", update);
        observerRef.current?.disconnect();
        observerRef.current = null;
      }

      elRef.current = el;

      if (el) {
        el.addEventListener("scroll", update, { passive: true });
        const observer = new ResizeObserver(update);
        observer.observe(el);
        observerRef.current = observer;
        update();
      }
    },
    [update],
  );

  const maskImage =
    showTop || showBottom
      ? [
          showTop
            ? `linear-gradient(to bottom, transparent, black ${fadeHeightPx}px)`
            : "linear-gradient(black, black)",
          showBottom
            ? `linear-gradient(to top, transparent, black ${fadeHeightPx}px)`
            : "linear-gradient(black, black)",
        ].join(", ")
      : undefined;

  return {
    ref: refCallback,
    style: {
      maskImage,
      maskSize: maskImage ? "100% 51%, 100% 51%" : undefined,
      maskPosition: maskImage ? "top, bottom" : undefined,
      maskRepeat: "no-repeat",
      WebkitMaskImage: maskImage,
      WebkitMaskSize: maskImage ? "100% 51%, 100% 51%" : undefined,
      WebkitMaskPosition: maskImage ? "top, bottom" : undefined,
      WebkitMaskRepeat: "no-repeat",
    },
  };
};
